import { useRef, useEffect, useState, type CSSProperties } from "react";
import { EditorState, StateEffect, StateField, Compartment, type Extension } from "@codemirror/state";
import { EditorView, Decoration, type DecorationSet, lineNumbers, highlightActiveLineGutter } from "@codemirror/view";
import { StreamLanguage } from "@codemirror/language";
import { highlightSelectionMatches } from "@codemirror/search";
import {
  type SourceDensity,
  DEFAULT_SOURCE_DENSITY,
  sourceDensityExtensions,
} from "@/lib/codemirror/source-density";
import {
  type SourceSurfaceId,
  DEFAULT_SOURCE_SURFACE,
  surfaceStyleVars,
} from "@/lib/codemirror/source-surface";
import { foldPreambleEffects, isUnfold, preambleFolding } from "@/lib/codemirror/preamble-fold";
import { latexRhythm, type RhythmOptions } from "@/lib/codemirror/latex-rhythm";
import styles from "./SourceCodeViewer.module.scss";

export type { SourceDensity };
export { DEFAULT_SOURCE_DENSITY };

interface SourceCodeViewerProps {
  value: string;
  language: StreamLanguage<unknown>;
  highlightRange?: { start: number; end: number };
  clickableLines?: Set<number>;
  onLineClick?: (line: number) => void;
  /**
   * S / M / L density — each is a *face pairing* at design sizes, not a
   * uniform scale (Charter only at L; Inter at S/M; mono near ~13).
   */
  density?: SourceDensity;
  /**
   * Wrap long lines, overriding the shared preference. A phone scrolls the
   * source sideways instead (View LaTeX, Olivia, 10 Oct).
   */
  wrap?: boolean;
  /**
   * A full document: open on the body, the preamble folded to one bar that
   * opens on a tap (the user, 11 Oct). Stays open across refreshes once opened.
   */
  foldPreamble?: boolean;
  /**
   * A full document's rhythm (Olivia, 11 Oct): fixed gaps between blocks
   * instead of the stored blank lines, visible-line numbers, the gutter and
   * padding sizes. Needs where each block starts.
   */
  rhythm?: RhythmOptions;
}

// LILIA-116 follow-up (2026-04-29): line numbers + word wrap toggles.
// Persisted as a single localStorage record so the prefs stick across
// sessions and across all viewer instances. Reading is synchronous and
// cheap; writes only happen on toggle.
interface ViewerPrefs {
  lineNumbers: boolean;
  wordWrap: boolean;
}

const PREFS_KEY = "lilia.editor.source-viewer-prefs";

function readPrefs(): ViewerPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        lineNumbers: parsed.lineNumbers !== false,
        wordWrap: parsed.wordWrap !== false,
      };
    }
  } catch { /* ignore */ }
  return { lineNumbers: true, wordWrap: true };
}

function writePrefs(prefs: ViewerPrefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
}

// Module-level pub-sub so a context-menu toggle in one viewer instance
// (or in a sibling component) updates every mounted viewer immediately.
type Listener = (prefs: ViewerPrefs) => void;
const listeners = new Set<Listener>();
function publishPrefs(prefs: ViewerPrefs) {
  writePrefs(prefs);
  for (const fn of listeners) fn(prefs);
}

export function getSourceViewerPrefs(): ViewerPrefs {
  return readPrefs();
}

export function toggleSourceViewerLineNumbers() {
  const next = readPrefs();
  next.lineNumbers = !next.lineNumbers;
  publishPrefs(next);
}

export function toggleSourceViewerWordWrap() {
  const next = readPrefs();
  next.wordWrap = !next.wordWrap;
  publishPrefs(next);
}

// === Active highlight (focused block) ===

const setHighlight = StateEffect.define<{ start: number; end: number } | null>();

const highlightField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setHighlight)) {
        if (!effect.value) return Decoration.none;
        const { start, end } = effect.value;
        const decos = [];
        const isSingle = start === end;
        for (let lineNum = start; lineNum <= end; lineNum++) {
          if (lineNum >= 1 && lineNum <= tr.state.doc.lines) {
            const line = tr.state.doc.line(lineNum);
            let cls = "cm-block-highlight cm-block-highlight-mid";
            if (isSingle) cls = "cm-block-highlight cm-block-highlight-single";
            else if (lineNum === start) cls = "cm-block-highlight cm-block-highlight-first";
            else if (lineNum === end) cls = "cm-block-highlight cm-block-highlight-last";
            decos.push(Decoration.line({ class: cls }).range(line.from));
          }
        }
        return Decoration.set(decos);
      }
    }
    // Map line decorations through doc edits (refresh/replace) so highlight
    // survives content sync without waiting for the next focusedBlockId change.
    return decorations.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

// === Clickable lines (block-mapped) ===

const setClickable = StateEffect.define<Set<number> | null>();

const clickableField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setClickable)) {
        if (!effect.value || effect.value.size === 0) return Decoration.none;
        const decos = [];
        for (const lineNum of effect.value) {
          if (lineNum >= 1 && lineNum <= tr.state.doc.lines) {
            const line = tr.state.doc.line(lineNum);
            decos.push(Decoration.line({ class: "cm-block-clickable" }).range(line.from));
          }
        }
        decos.sort((a, b) => a.from - b.from);
        return Decoration.set(decos);
      }
    }
    return decorations.map(tr.changes);
  },
  provide: (f) => EditorView.decorations.from(f),
});

// === Theme ===

const highlightTheme = EditorView.baseTheme({
  // Active highlight (focused block)
  // Block sync wash — colors from --src-* surface presets on the viewer root.
  ".cm-block-highlight": {
    backgroundColor: "var(--src-block-bg, rgba(15, 108, 189, 0.09))",
    borderLeft: "2px solid var(--src-block-border, rgba(15, 108, 189, 0.50))",
  },
  ".cm-block-highlight-first": {
    borderTop: "1px solid color-mix(in srgb, var(--src-block-border, rgba(15, 108, 189, 0.50)) 55%, transparent)",
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "4px",
  },
  ".cm-block-highlight-last": {
    borderBottom: "1px solid color-mix(in srgb, var(--src-block-border, rgba(15, 108, 189, 0.50)) 55%, transparent)",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
  },
  ".cm-block-highlight-mid": {},
  ".cm-block-highlight-single": {
    border: "1px solid color-mix(in srgb, var(--src-block-border, rgba(15, 108, 189, 0.50)) 55%, transparent)",
    borderLeft: "2px solid var(--src-block-border, rgba(15, 108, 189, 0.50))",
    borderRadius: "4px",
  },
  ".cm-block-clickable": {
    cursor: "pointer",
    transition: "background-color 0.15s ease",
  },
  ".cm-block-clickable:hover": {
    backgroundColor: "var(--src-block-hover, rgba(15, 108, 189, 0.05))",
  },
  ".cm-block-highlight.cm-block-clickable:hover": {
    backgroundColor: "var(--src-block-bg, rgba(15, 108, 189, 0.09))",
  },
});

function isDarkDocument(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.classList.contains("dark");
}

export function SourceCodeViewer({
  value,
  language,
  highlightRange,
  clickableLines,
  onLineClick,
  density = DEFAULT_SOURCE_DENSITY,
  wrap,
  foldPreamble = false,
  rhythm,
}: SourceCodeViewerProps) {
  const rhythmCompartment = useRef(new Compartment());
  const rhythmKey = rhythm ? `${rhythm.phone ? "p" : "d"}:${rhythm.blockStarts.join(",")}` : "";
  const preambleOpened = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onLineClickRef = useRef(onLineClick);
  onLineClickRef.current = onLineClick;

  // Re-resolve surface CSS vars when dark class flips (no remount).
  const [isDark, setIsDark] = useState(isDarkDocument);
  useEffect(() => {
    const root = document.documentElement;
    const obs = new MutationObserver(() => setIsDark(root.classList.contains("dark")));
    obs.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Compartments hold line-numbers, wrap, and density (theme + dual-type
  // highlighting) so S/M/L can swap faces + sizes without remounting CM.
  const lineNumbersCompartment = useRef(new Compartment());
  const wordWrapCompartment = useRef(new Compartment());
  const densityCompartment = useRef(new Compartment());

  // Subscribe to the shared prefs store so any toggle (from this
  // instance OR another mounted viewer) re-applies the new settings.
  const [prefs, setPrefs] = useState<ViewerPrefs>(() => readPrefs());
  useEffect(() => {
    const handler: Listener = (next) => setPrefs(next);
    listeners.add(handler);
    return () => { listeners.delete(handler); };
  }, []);

  // Apply prefs to the running editor whenever they change.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: [
        lineNumbersCompartment.current.reconfigure(prefs.lineNumbers && !rhythm ? lineNumbers() : []),
        wordWrapCompartment.current.reconfigure((wrap ?? prefs.wordWrap) ? EditorView.lineWrapping : []),
      ],
    });
  }, [prefs, wrap]);

  // Block starts move when the document changes: the gaps follow.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({ effects: rhythmCompartment.current.reconfigure(rhythm ? latexRhythm(rhythm) : []) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rhythmKey]);

  // Swap density preset: content face + structure face + both sizes.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: densityCompartment.current.reconfigure(sourceDensityExtensions(density)),
    });
    view.requestMeasure();
  }, [density]);

  useEffect(() => {
    if (!containerRef.current) return;

    const initial = readPrefs();
    const extensions: Extension[] = [
      EditorView.editable.of(false),
      EditorState.readOnly.of(true),
      lineNumbersCompartment.current.of(initial.lineNumbers && !rhythm ? lineNumbers() : []),
      rhythmCompartment.current.of(rhythm ? latexRhythm(rhythm) : []),
      highlightActiveLineGutter(),
      highlightSelectionMatches(),
      language,
      // Density owns theme + dual-type highlighting (per-face sizes).
      densityCompartment.current.of(sourceDensityExtensions(density)),
      wordWrapCompartment.current.of((wrap ?? initial.wordWrap) ? EditorView.lineWrapping : []),
      highlightField,
      clickableField,
      highlightTheme,
      foldPreamble
        ? [
            preambleFolding(),
            EditorView.updateListener.of((u) => {
              if (u.transactions.some(isUnfold)) preambleOpened.current = true;
            }),
          ]
        : [],
      EditorView.domEventHandlers({
        click(event, view) {
          if (!onLineClickRef.current) return false;
          const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
          if (pos === null) return false;
          const line = view.state.doc.lineAt(pos);
          onLineClickRef.current(line.number);
          return false;
        },
      }),
    ];

    const state = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;
    if (foldPreamble) view.dispatch({ effects: foldPreambleEffects(view.state) });

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync content when value changes. Re-apply highlight + clickable maps
  // after a full replace so block↔line sync stays live after auto-refresh.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const currentDoc = view.state.doc.toString();
    if (currentDoc !== value) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: value },
        effects: [
          setHighlight.of(highlightRange ?? null),
          setClickable.of(clickableLines ?? null),
        ],
      });
      if (foldPreamble && !preambleOpened.current) view.dispatch({ effects: foldPreambleEffects(view.state) });
    }
    // highlightRange / clickableLines applied here only after content replace;
    // their own effects handle ordinary focus/map updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Sync highlight range (left canvas → source)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    view.dispatch({
      effects: setHighlight.of(highlightRange ?? null),
    });

    if (highlightRange && highlightRange.start >= 1 && highlightRange.start <= view.state.doc.lines) {
      const line = view.state.doc.line(highlightRange.start);
      view.dispatch({
        effects: EditorView.scrollIntoView(line.from, { y: "center" }),
      });
    }
  }, [highlightRange]);

  // Sync clickable lines (source → left canvas)
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: setClickable.of(clickableLines ?? null),
    });
  }, [clickableLines]);

  // One surface. The five-way picker was removed on 14 Sep 2026 — see
  // lib/codemirror/source-surface.ts.
  const surfaceStyle = surfaceStyleVars(isDark);

  return (
    <div
      ref={containerRef}
      className={styles.viewer}
      data-density={density}
            data-testid="source-code-viewer"
      style={surfaceStyle as CSSProperties}
    />
  );
}
